import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RefreshCw, AlertCircle } from 'lucide-react';

const ImageViewer = ({
  loading,
  error,
  imageUrl,
  isDarkMode,
  currentTableData,
  currentSelection,
  onImageChange,
  images = [], // Array of { id, url, name, displayType }
  currentImageIndex = 0
}) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const viewerRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const initialTableZoomAppliedRef = useRef(false);

  // Reset error state when image URL changes
  useEffect(() => {
    setLoadError(null);
  }, [imageUrl]);

  // Calculate optimal zoom and position
  const calculateOptimalView = useCallback(() => {
    if (!containerRef.current || !imageDimensions) return { zoom: 1, x: 0, y: 0 };
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // Calculate zoom to fit the entire image
    const zoomX = containerWidth / imageDimensions.width;
    const zoomY = containerHeight / imageDimensions.height;
    let newZoom = Math.min(zoomX, zoomY) * 0.95; // 95% of fit to leave a small margin
    
    // Ensure zoom is not too small for very large images
    newZoom = Math.max(newZoom, 0.1);
    
    // Calculate position to center the image
    const scaledWidth = imageDimensions.width * newZoom;
    const scaledHeight = imageDimensions.height * newZoom;
    
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;
    
    return { zoom: newZoom, x, y };
  }, [imageDimensions]);

  // Function to pan to a specific bounding box
  const panToBox = useCallback((boundingBox) => {
    if (!boundingBox || !imageDimensions || !containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // Calculate position of the bounding box in absolute pixels
    const boxX = boundingBox.Left * imageDimensions.width;
    const boxY = boundingBox.Top * imageDimensions.height;
    const boxWidth = boundingBox.Width * imageDimensions.width;
    const boxHeight = boundingBox.Height * imageDimensions.height;
    
    // Calculate zoom level to make the box visible with plenty of context
    // Use a much lower zoom factor to show more of the surrounding image
    const zoomX = containerWidth / (boxWidth * 5); // Show 5x the width of the box
    const zoomY = containerHeight / (boxHeight * 5); // Show 5x the height of the box
    
    // Constrain zoom to a reasonable range
    const newZoom = Math.min(Math.max(zoomX, zoomY, 0.2), 1.0); // Never zoom in more than 1:1
    
    // Calculate position to center the bounding box
    const centerX = boxX + boxWidth / 2;
    const centerY = boxY + boxHeight / 2;
    
    const newX = containerWidth / 2 - centerX * newZoom;
    const newY = containerHeight / 2 - centerY * newZoom;
    
    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  }, [imageDimensions]);

  // Reset position when image changes
  useEffect(() => {
    if (imageUrl) {
      initialTableZoomAppliedRef.current = false;
      setZoom(0.5); // Default zoom
      setPosition({ x: 0, y: 0 });
    }
  }, [imageUrl]);

  // Handle when selection changes - pan to encompass all selected cells
  useEffect(() => {
    if (currentSelection?.selectedCells?.length && currentTableData?.textractMetadata?.cells) {
      // Check if ANY of the selected cells have valid bounding box data
      const cellsWithValidBBox = currentSelection.selectedCells.filter(({ row, col }) => {
        const metadata = currentTableData.textractMetadata.cells[row]?.[col];
        return metadata?.boundingBox && 
               metadata.boundingBox.Width > 0 && 
               metadata.boundingBox.Height > 0;
      });
      
      // If none of the cells have valid bounding boxes, don't change the pan/zoom
      if (cellsWithValidBBox.length === 0) {
        // console.log('No valid bounding boxes in selection, maintaining current view');
        return;
      }
      
      // Calculate bounding box for valid cells only
      let minLeft = 1, minTop = 1, maxRight = 0, maxBottom = 0;
      
      cellsWithValidBBox.forEach(({ row, col }) => {
        const metadata = currentTableData.textractMetadata.cells[row]?.[col];
        const { Left, Top, Width, Height } = metadata.boundingBox;
        minLeft = Math.min(minLeft, Left);
        minTop = Math.min(minTop, Top);
        maxRight = Math.max(maxRight, Left + Width);
        maxBottom = Math.max(maxBottom, Top + Height);
      });
      
      // Create a combined bounding box with margin
      const combinedBox = {
        Left: Math.max(0, minLeft - 0.05),
        Top: Math.max(0, minTop - 0.05),
        Width: Math.min(1 - minLeft, maxRight - minLeft + 0.1),
        Height: Math.min(1 - minTop, maxBottom - minTop + 0.1)
      };
      
      panToBox(combinedBox);
    }
  }, [currentSelection, currentTableData, panToBox]);

  // Pan to table when table changes
  useEffect(() => {
    // Only zoom to the table on first load
    if (currentTableData?.boundingBox && !initialTableZoomAppliedRef.current) {
      // Add a small margin around the table
      const boundingBox = { ...currentTableData.boundingBox };
      
      // Skip invalid bounding boxes
      if (boundingBox.Width <= 0 || boundingBox.Height <= 0) {
        console.log('Skipping initial zoom - invalid bounding box');
        initialTableZoomAppliedRef.current = true; // Still mark as applied
        return;
      }
      
      // Add 10% margin
      const margin = 0.1;
      boundingBox.Left = Math.max(0, boundingBox.Left - boundingBox.Width * margin);
      boundingBox.Top = Math.max(0, boundingBox.Top - boundingBox.Height * margin);
      boundingBox.Width = Math.min(1 - boundingBox.Left, boundingBox.Width * (1 + 2 * margin));
      boundingBox.Height = Math.min(1 - boundingBox.Top, boundingBox.Height * (1 + 2 * margin));
      
      panToBox(boundingBox);
      initialTableZoomAppliedRef.current = true;
      console.log('Initial table zoom applied');
    }
  }, [currentTableData, panToBox]);

  const handleZoomIn = () => {
    if (!containerRef.current) return;
    
    // Zoom in to the center of the current view
    const container = containerRef.current;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2;
    
    // Calculate position relative to current transform
    const relativeX = (centerX - position.x) / zoom;
    const relativeY = (centerY - position.y) / zoom;
    
    // New zoom level
    const newZoom = Math.min(zoom + 0.1, 3);
    
    // Calculate new position to keep center point fixed
    const newX = centerX - relativeX * newZoom;
    const newY = centerY - relativeY * newZoom;
    
    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  };
  
  // Similarly modify handleZoomOut:
  const handleZoomOut = () => {
    if (!containerRef.current) return;
    
    // Zoom out from the center of the current view
    const container = containerRef.current;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2;
    
    // Calculate position relative to current transform
    const relativeX = (centerX - position.x) / zoom;
    const relativeY = (centerY - position.y) / zoom;
    
    // New zoom level
    const newZoom = Math.max(zoom - 0.1, 0.1);
    
    // Calculate new position to keep center point fixed
    const newX = centerX - relativeX * newZoom;
    const newY = centerY - relativeY * newZoom;
    
    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  };

  const handleReset = () => {
    if (!imageDimensions || !containerRef.current) return;
    
    const { zoom: newZoom, x, y } = calculateOptimalView();
    setZoom(newZoom);
    setPosition({ x, y });
  };

  useEffect(() => {
    // Function to adjust controls position based on thumbnail bar state
    const adjustControlsPosition = () => {
      const thumbnailBar = document.querySelector('.thumbnail-bar');
      const controlsElement = document.querySelector('.image-controls');
      
      if (!thumbnailBar || !controlsElement) return;
      
      const thumbnailHeight = thumbnailBar.offsetHeight;
      const isExpanded = thumbnailHeight > 50; // Check if expanded (more than header height)
      
      // Set position based on thumbnail bar state
      controlsElement.style.bottom = isExpanded ? `${thumbnailHeight + 16}px` : '16px';
    };
    
    // Initial adjustment
    adjustControlsPosition();
    
    // Set up observer to watch for changes to the thumbnail bar height
    const observer = new ResizeObserver(adjustControlsPosition);
    const thumbnailBar = document.querySelector('.thumbnail-bar');
    
    if (thumbnailBar) {
      observer.observe(thumbnailBar);
    }
    
    return () => {
      if (thumbnailBar) {
        observer.unobserve(thumbnailBar);
      }
      observer.disconnect();
    };
  }, []);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left mouse button
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      
      // Get mouse position relative to container
      const containerRect = viewerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const mouseY = e.clientY - containerRect.top;
      
      // Calculate mouse position relative to current transform
      const relativeX = (mouseX - position.x) / zoom;
      const relativeY = (mouseY - position.y) / zoom;
      
      // Determine new zoom level
      const zoomDelta = 0.1;
      const newZoom = e.deltaY < 0 
        ? Math.min(zoom + zoomDelta, 3) // Zoom in
        : Math.max(zoom - zoomDelta, 0.1); // Zoom out
      
      // Calculate new position to keep mouse point fixed
      const newX = mouseX - relativeX * newZoom;
      const newY = mouseY - relativeY * newZoom;
      
      setZoom(newZoom);
      setPosition({ x: newX, y: newY });
    };
  
    const viewerElement = viewerRef.current;
    if (viewerElement) {
      viewerElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => viewerElement.removeEventListener('wheel', handleWheel);
    }
  }, [zoom, position]);

  const navigateImage = (direction) => {
    let newIndex = currentImageIndex + direction;
    
    // Wrap around
    if (newIndex < 0) newIndex = images.length - 1;
    if (newIndex >= images.length) newIndex = 0;
    
    if (onImageChange) {
      onImageChange(newIndex);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'ArrowLeft') {
        navigateImage(-1);
      } else if (e.ctrlKey && e.key === 'ArrowRight') {
        navigateImage(1);
      } else if (e.key === '+') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === '0') {
        handleReset();
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, images.length]);

  // Get current image details
  const currentImage = images[currentImageIndex] || {};
  
  // Check if current image is a converted TIF
  const isTif = currentImage.fileType === 'tif';
  const isConverted = currentImage.displayType === 'png' && isTif;

  const handleImageError = () => {
    setLoadError(`Unable to display image. ${isTif ? 'TIF file will be converted to PNG.' : ''}`);
  };

  if (loading || (images[currentImageIndex] && images[currentImageIndex].status === "loading")) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        {error}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <AlertCircle size={24} className="text-amber-500 mb-2" />
        <div className={`text-center ${isDarkMode ? 'text-amber-400' : 'text-amber-600'} max-w-md px-4`}>
          {loadError}
          {isTif && !isConverted && (
            <div className="mt-2 text-sm">
              <div className="animate-pulse">
                Conversion in progress... This may take a moment.
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="mt-3 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!imageUrl && (!images[currentImageIndex] || images[currentImageIndex].status !== "loading")) {
    return (
      <div className={`flex items-center justify-center h-full ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No image selected
      </div>
    );
  }

  return (
    <div 
      className="h-full w-full relative overflow-hidden"
      ref={viewerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {/* Main image container */}
      <div
        ref={containerRef}
        className="absolute inset-0"
      >
        <div
          className={`absolute transform-gpu ${!isDragging ? 'transition-transform duration-300' : ''}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0'
          }}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Table visual"
            className="max-w-none"
            draggable="false"
            onError={handleImageError}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.target;
              setImageDimensions({ width: naturalWidth, height: naturalHeight });
              
              // Wait a moment for the container size to stabilize
              setTimeout(() => {
                handleReset();
              }, 200);
            }}
          />
          
          {/* Overlay for selected cells */}
          {currentTableData && currentSelection?.selectedCells?.length > 0 && (
            <div className="absolute inset-0 pointer-events-none">
              {currentSelection.selectedCells.map((cell, i) => {
                const metadata = currentTableData.textractMetadata.cells[cell.row]?.[cell.col];
                if (!metadata || !metadata.boundingBox) return null;
                
                const { boundingBox } = metadata;
                
                return (
                  <div
                    key={i}
                    className="absolute border-2 border-blue-500 bg-blue-500/20"
                    style={{
                      left: `${boundingBox.Left * 100}%`,
                      top: `${boundingBox.Top * 100}%`,
                      width: `${boundingBox.Width * 100}%`,
                      height: `${boundingBox.Height * 100}%`
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls */}
      <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black/30 rounded-full px-3 py-1.5 backdrop-blur-sm z-20 image-controls">        {images.length > 1 && (
          <button
            onClick={() => navigateImage(-1)}
            className="p-1.5 text-white hover:bg-white/20 rounded-full"
            title="Previous image"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        
        <button
          onClick={handleZoomOut}
          className="p-1.5 text-white hover:bg-white/20 rounded-full"
          title="Zoom out"
        >
          <ZoomOut size={18} />
        </button>
        
        <button
          onClick={handleReset}
          className="p-1.5 text-white hover:bg-white/20 rounded-full"
          title="Reset view"
        >
          <RefreshCw size={18} />
        </button>
        
        <button
          onClick={handleZoomIn}
          className="p-1.5 text-white hover:bg-white/20 rounded-full"
          title="Zoom in"
        >
          <ZoomIn size={18} />
        </button>
        
        {images.length > 1 && (
          <button
            onClick={() => navigateImage(1)}
            className="p-1.5 text-white hover:bg-white/20 rounded-full"
            title="Next image"
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {/* Image counter and type indicator */}
      <div className="absolute top-4 left-4 bg-black/30 text-white text-sm px-2 py-1 rounded-md backdrop-blur-sm flex items-center gap-2">
        {images.length > 1 && (
          <span>
            {currentImageIndex + 1} / {images.length}
          </span>
        )}
        {isConverted && (
          <span className=" text-white text-xs px-1.5 py-0.5 rounded-sm">
            Converted TIF
          </span>
        )}
      </div>
    </div>
  );
};

export default ImageViewer;